import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FileText, X } from 'lucide-react';
import { neonQuery } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Bills sitting on YOUR desk, on every page.
 *
 * Greg, 2026-09-02: "I'm in CPx a lot... can we change that to an alert if there's
 * invoices waiting for my approval? That would help a lot. And you can show it on
 * any page... just, you're going to have to make sure I don't have to hard refresh
 * the page to get it. It's going to have to insert itself whenever one gets entered
 * and makes it to the final step for me."
 *
 * ⚠️ IT TARGETS ITSELF, so there is no role to configure and nobody to keep in step.
 * The query asks "which bills are at a step whose approver is me", so the banner
 * exists for exactly the people who have work and nobody else. A rule with a name in
 * it would have to be maintained the next time the chain changes.
 *
 * ⚠️ IT POLLS, because the alternative is a page that only tells the truth when you
 * remember to reload. Sixty seconds, one indexed count, and it re-checks the moment
 * the tab is focused again - which is the case that actually matters, since he is
 * moving between this and Outlook all day and a bill that landed while he was away
 * should be there when he looks back.
 *
 * ⚠️ DISMISSING IS FOR THIS COUNT ONLY, NOT FOR THE DAY. If it could be silenced
 * outright it would eventually be silenced permanently, and then it is not an alert.
 * Dismissing hides the number he has already seen; a new bill brings it straight
 * back, because the count is part of what is dismissed.
 */
export default function ApprovalsWaitingBanner({ onOpen }) {
  const [state, setState] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(null); // the count that was dismissed
  const timer = useRef(null);
  const lastLoad = useRef(0);
  // Desktop only. Greg, 2026-10-20: "I'm not going to be approving invoices for mobile."
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    try {
      const rows = await neonQuery('ap_waiting_on_me', {});
      const row = Array.isArray(rows) ? rows[0] : (rows && rows.rows && rows.rows[0]);
      setState(row || null);
    } catch {
      // A failed count must never put a wrong number on every page in the app.
      setState(null);
    }
  }, []);

  useEffect(() => {
    lastLoad.current = Date.now();
    load();
    timer.current = setInterval(() => { lastLoad.current = Date.now(); load(); }, 60_000);
    // ⚠️ COMING BACK TO THE TAB FIRES BOTH `focus` AND `visibilitychange`, so an
    // un-throttled handler is two requests every time he switches away from Outlook
    // and back - from every open CPx tab. This runs app-wide, so a small sloppiness
    // here is multiplied by every page and every tab.
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastLoad.current < 15_000) return;
      lastLoad.current = Date.now();
      load();
    };
    // Matt, 2026-10-13: approved a crab invoice and the banner still said it was
    // waiting. The count was right; it just had not been asked again yet. A bill
    // changed in THIS tab re-counts straight away (api.js fires the event).
    const onChanged = () => { lastLoad.current = Date.now(); load(); };
    window.addEventListener('cpx:approvals-changed', onChanged);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(timer.current);
      window.removeEventListener('cpx:approvals-changed', onChanged);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  if (isMobile) return null;
  const count = Number(state && state.waiting) || 0;
  if (!count) return null;
  if (dismissedAt === count) return null;

  const total = Number(state && state.total) || 0;
  const oldest = Number(state && state.oldest_days) || 0;
  const money = total.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div style={{
      background: '#fff7f2', borderBottom: '1px solid #f6d4b8', color: '#9a4a1c',
      fontSize: 13, padding: '8px 14px', display: 'flex', alignItems: 'center',
      gap: 10, flexShrink: 0, fontFamily: 'inherit',
    }}>
      <FileText size={16} style={{ color: 'var(--orange, #DC582A)', flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>
          {count} {count === 1 ? 'invoice is' : 'invoices are'} waiting for your approval
        </strong>
        {total > 0 && <> · {money}</>}
        {/* ⚠️ THE OLDEST ONE IS THE FACT THAT CHANGES BEHAVIOUR. A count alone reads the
            same on the day a bill arrives as it does a fortnight later, and a crabber
            waiting a fortnight is the thing this whole chain exists to prevent. */}
        {oldest >= 2 && (
          <> · the oldest has been with you <strong>{oldest} days</strong></>
        )}
      </span>
      <button
        onClick={() => onOpen && onOpen()}
        className="btn btn-outline"
        style={{ fontSize: 12, padding: '3px 10px', flexShrink: 0 }}
      >
        Go to them
      </button>
      <button
        onClick={() => setDismissedAt(count)}
        title="Hide until something changes"
        style={{
          background: 'none', border: 'none', color: '#9a4a1c', cursor: 'pointer',
          padding: 2, display: 'flex', flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
